import { useCallback, useRef, useState } from 'react';
import type { ProcessEdge, ProcessNode } from '../graphTypes';

/**
 * One frame of canvas history. The whole nodes/edges arrays are captured
 * by reference -- React Flow's mutators always produce fresh arrays so a
 * cheap reference snapshot is enough to restore the previous state.
 */
export interface GraphSnapshot {
  readonly nodes: readonly ProcessNode[];
  readonly edges: readonly ProcessEdge[];
}

/**
 * Maximum number of frames retained on the past stack. Anything older
 * than this is dropped when a new push tips the stack over the limit.
 * 50 is roughly five minutes of typing at the auto-save cadence -- past
 * that point the user almost certainly wants the undo trail to be cheap
 * rather than infinite.
 */
const HISTORY_CAP = 50;

export interface UseGraphHistoryReturn {
  /** True when a previous snapshot is available to restore. */
  readonly canUndo: boolean;
  /** True when a forward snapshot is available to restore. */
  readonly canRedo: boolean;
  /**
   * Capture the current graph as a new "before" frame on the past stack.
   * Clears the future stack -- a fresh edit invalidates any redo trail.
   * Caller passes the *pre-mutation* snapshot so undo restores what the
   * user was looking at right before the change.
   */
  push: (snapshot: GraphSnapshot) => void;
  /**
   * Restore the most recent past frame. The current state is moved to
   * the future stack so it can be redone. Returns the snapshot to
   * apply, or null if there's nothing to undo.
   */
  undo: (current: GraphSnapshot) => GraphSnapshot | null;
  /**
   * Restore the most recent future frame. The current state is moved
   * back to the past stack so it can be undone again. Returns the
   * snapshot to apply, or null if there's nothing to redo.
   */
  redo: (current: GraphSnapshot) => GraphSnapshot | null;
  /** Clear both stacks. Called on process switch / fresh seed. */
  reset: () => void;
}

/**
 * Snapshot-based undo/redo for the process editor canvas. The hook only
 * tracks {@link GraphSnapshot}s -- it intentionally knows nothing about
 * how the snapshots become live state. The owner ({@code useProcessGraph})
 * calls {@link push} immediately before applying a mutation and uses the
 * value returned from {@link undo}/{@link redo} to reseed its `nodes` +
 * `edges` state.
 *
 * Stacks live in refs (not state) so push/undo/redo never trigger an
 * extra render on their own; the {@code canUndo}/{@code canRedo} booleans
 * mirror the ref lengths via {@code useState} so consumers re-render
 * exactly when the affordance availability changes.
 */
export function useGraphHistory(): UseGraphHistoryReturn {
  const past = useRef<GraphSnapshot[]>([]);
  const future = useRef<GraphSnapshot[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const sync = useCallback(() => {
    setCanUndo(past.current.length > 0);
    setCanRedo(future.current.length > 0);
  }, []);

  const push = useCallback(
    (snapshot: GraphSnapshot) => {
      past.current.push(snapshot);
      if (past.current.length > HISTORY_CAP) {
        past.current.splice(0, past.current.length - HISTORY_CAP);
      }
      // A new edit always invalidates the redo trail; the user has
      // committed to a different branch of the timeline.
      if (future.current.length > 0) future.current = [];
      sync();
    },
    [sync],
  );

  const undo = useCallback(
    (current: GraphSnapshot): GraphSnapshot | null => {
      const previous = past.current.pop();
      if (!previous) return null;
      future.current.push(current);
      sync();
      return previous;
    },
    [sync],
  );

  const redo = useCallback(
    (current: GraphSnapshot): GraphSnapshot | null => {
      const next = future.current.pop();
      if (!next) return null;
      past.current.push(current);
      sync();
      return next;
    },
    [sync],
  );

  const reset = useCallback(() => {
    past.current = [];
    future.current = [];
    sync();
  }, [sync]);

  return { canUndo, canRedo, push, undo, redo, reset };
}
